/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : pageHelp registry
 *   Feature      : 226-universal-page-affordances
 *   Description  : Per-page contextual help content, keyed by a stable pageId
 *                  (route slug). Rendered by <PageHelpButton helpId=…> via the
 *                  shared PageHelpPanel. One entry per routed content page; the
 *                  conformance guard requires every non-exempt page to have one
 *                  with a real (non-placeholder) purpose.
 *   Constitution : v1.23.0
 *   Author       : Peter Dobler
 *   Created      : 2026-07-07
 * ============================================================================
 */
import type { PageHelpContent } from '../../../components/ui/PageHelpPanel';

/**
 * Master page-help registry: pageId -> PageHelpContent.
 * pageId convention = the route slug (e.g. 'dashboard.admin', 'insights.capacity').
 * Content authored per Spec 226 US1; long tail is real minimal prose (never a
 * placeholder token — Principle XLI).
 */
export const pageHelp: Record<string, PageHelpContent> = {
  'dashboard.admin': {
    pageTitle: 'Admin Dashboard',
    purpose:
      'Fleet-wide operational health at a glance — the KPI tiles, alerts, capacity utilization and collection status the platform admin acts on.',
    sections: [
      {
        title: 'What the tiles mean',
        concepts: [
          { term: 'Critical Alerts', definition: 'Unresolved alerts at critical severity across the fleet.' },
          { term: 'Capacity Health', definition: 'Percentage of capacities reporting a healthy status.' },
          { term: 'Backup Success', definition: 'Completed backups ÷ total scheduled for the current window.' },
          { term: 'Active Users', definition: 'Unique users who logged in today, vs total registered.' },
        ],
      },
      {
        title: 'Freshness',
        paragraphs: [
          'Tiles show DB-collected data. "Updated" is when this page last fetched; "collected" is when the underlying data was last gathered by the collectors — it can be older.',
        ],
      },
    ],
  },
  'insights.capacity': {
    pageTitle: 'Capacity Insights',
    purpose:
      'Is the fleet healthy, is anything saturated, and is every capacity the right size — from collected CU telemetry and list prices.',
    sections: [
      {
        title: 'Headline KPIs',
        concepts: [
          { term: 'Fleet Avg (24h)', definition: 'Mean CU% across all active capacities over the last 24 hours.' },
          { term: 'Fleet Peak (24h)', definition: 'Highest single CU% reading any capacity hit in the last 24 hours.' },
          { term: 'Paused', definition: 'Capacities currently paused (no compute billed; no telemetry collected while paused).' },
          { term: 'Active Capacities', definition: 'Capacities reporting CU telemetry — the population the fleet figures are computed over.' },
        ],
      },
      {
        title: 'Capacities above 80% CU',
        paragraphs: [
          'How many DISTINCT capacities had at least one CU reading over 80% in each window (Now / Last hour / 24h / 7 days / 30 days). "Now" looks only at each capacity\'s single latest reading.',
          'The table below ranks the worst offenders over the last 30 days so you can see which capacities to look at first.',
        ],
      },
      {
        title: 'Trends & per-capacity charts',
        concepts: [
          { term: 'Fleet utilization — 60 days', definition: 'Daily fleet-wide max/peak CU% trend, so you can see saturation building over time.' },
          { term: 'Intraday — last 24 hours', definition: 'A fine-grained 24h sparkline of fleet CU%.' },
          { term: 'Per-capacity breakdown — 60 days', definition: 'Avg CU% and Peak CU% per capacity (filterable in/out) to compare capacities directly.' },
        ],
      },
      {
        title: 'Right-sizing verdicts',
        paragraphs: [
          'Rules: sustained peak < 40% → downsize · p95 > 80% sustained ≥ 7 days → upgrade · paused / no telemetry → not collected.',
          'Each verdict carries a real $ cost delta from the collected capacity SKU rates (blank when a rate has not been collected).',
        ],
      },
      {
        title: 'Efficiency ranking',
        concepts: [
          { term: 'Efficiency score', definition: '(average CU% ÷ $/hr) scaled 0–100 against the single most cost-efficient capacity in your fleet (100 = best utilization-per-dollar today).' },
          { term: 'Low score ≠ wrong-sized', definition: 'A low score may just mean it costs more per hour for the same utilization — check the right-sizing verdict for sizing.' },
        ],
      },
      {
        title: 'Throttling & fingerprint',
        concepts: [
          { term: 'Throttling', definition: 'Raw throttling readings (last 7 days) plus a throttling-over-time view — where CU demand exceeded capacity.' },
          { term: 'Usage fingerprint', definition: 'A 7×24 heat grid of mean CU% by weekday × hour (60 days) for a selected capacity — reveals daily/weekly load patterns.' },
          { term: 'What runs where', definition: 'Which subscriptions / workspaces sit on each capacity.' },
        ],
      },
      {
        title: 'Freshness & source',
        paragraphs: [
          'All figures come from collected CU telemetry (Eventstream) and collected SKU/list prices — not a live billing feed. "Updated" is when this page last fetched; the data itself is as fresh as the last collection run.',
        ],
      },
    ],
  },
  'insights.workspace': {
    pageTitle: 'Workspace Insights',
    purpose:
      'How Fabric workspaces are distributed and sized across the estate — built on real collected signals, not guessed activity.',
    sections: [
      {
        title: 'Headline KPIs',
        concepts: [
          { term: 'Total Workspaces', definition: 'All active workspaces collected across the tenant.' },
          { term: 'Client-assigned', definition: 'Workspaces bound to a specific client.' },
          { term: 'Shared', definition: 'Workspaces serving the central/shared fabric (not client-specific).' },
          { term: 'Avg Items / Workspace', definition: 'Mean collected item count per workspace.' },
          { term: 'With Semantic Models', definition: 'Workspaces that contain at least one semantic model.' },
          { term: 'Empty (0 items)', definition: 'Workspaces with no collected items — cleanup candidates.' },
        ],
      },
      {
        title: 'Breakdowns',
        concepts: [
          { term: 'Client-assigned vs shared', definition: 'Split of the fleet between client-bound and shared workspaces.' },
          { term: 'Workspaces by capacity', definition: 'Which capacity each workspace sits on — spots capacity concentration.' },
          { term: 'By workspace type', definition: 'Distribution by workspace type.' },
          { term: 'Largest workspaces by storage', definition: 'Ranked by latest collected storage size (from snapshots).' },
        ],
      },
      {
        title: 'Trends & explorer',
        concepts: [
          { term: 'Cumulative workspace growth — 60 days', definition: 'Running total of workspaces over time from daily snapshots.' },
          { term: 'Empty workspaces', definition: 'The full list of item-less workspaces.' },
          { term: 'Workspace explorer', definition: 'A searchable, filterable table of every workspace with its key attributes.' },
        ],
      },
      {
        title: 'Note',
        paragraphs: ['This page was rebuilt on real signals — the old "dormant/active/orphaned" framing was dropped because last-activity is not collected. All figures reflect the latest collection run.'],
      },
    ],
  },
  'insights.item': {
    pageTitle: 'Fabric Item Insights',
    purpose:
      'What Fabric items exist across the tenant, what changed recently, and what is stale, unrefreshed, or unprotected.',
    sections: [
      {
        title: 'Headline KPIs',
        concepts: [
          { term: 'Total Items', definition: 'All collected Fabric items across the tenant.' },
          { term: 'Created This Month', definition: 'Items whose created date falls in the current month.' },
          { term: 'Modified This Week', definition: 'Items with a modified date in the last 7 days (from the scanner).' },
          { term: 'Refresh-Failing Models', definition: 'Semantic models whose latest refresh failed.' },
          { term: 'Unprotected LH/WH', definition: 'Lakehouses/warehouses with no backup protection.' },
        ],
      },
      {
        title: 'Census & mix',
        concepts: [
          { term: 'Item census by type', definition: 'Counts grouped by Fabric type (notebook, pipeline, report, semantic model, …).' },
          { term: 'Notebook language mix', definition: 'Notebooks by language (from getDefinition; converges over daily runs).' },
          { term: 'Reports by type / Endorsed reports', definition: 'Report distribution and which carry an endorsement.' },
          { term: 'Pipelines', definition: 'Data pipelines and their detail.' },
        ],
      },
      {
        title: 'Attention lists',
        concepts: [
          { term: 'Refresh-failing semantic models', definition: 'Models with a failing latest refresh — act on these first.' },
          { term: 'Row-count leaders', definition: 'Largest models by measured row count (Import-mode; DirectLake shows no count).' },
          { term: 'Staleness ranking — notebooks', definition: 'Notebooks ranked by how long since last modified.' },
        ],
      },
    ],
  },
  'insights.warehouse': {
    pageTitle: 'Warehouse Insights',
    purpose:
      'Warehouse storage and table trends across the estate, with workspace/capacity attribution and backup coverage.',
    sections: [
      {
        title: 'Headline KPIs',
        concepts: [
          { term: 'Total Warehouses', definition: 'All active warehouses collected.' },
          { term: 'Total Storage (GB)', definition: 'Sum of latest measured warehouse sizes.' },
          { term: 'Backup-Protected / Protected %', definition: 'Warehouses with a backup plan, and that share of the fleet.' },
          { term: 'Measured Coverage', definition: 'Share of warehouses whose size was actually measured this run (a miss shows blank, never a fake 0).' },
        ],
      },
      {
        title: 'Trends',
        concepts: [
          { term: 'Fleet storage — 60 days', definition: 'Carry-forward total measured size over time (blank where a measurement was missed, not zero).' },
          { term: 'Table count — 60 days', definition: 'Total warehouse table count trend.' },
          { term: 'Growth ranking — 60 days', definition: 'Warehouses ranked by storage growth over the window.' },
        ],
      },
      {
        title: 'Lists',
        concepts: [
          { term: 'Top by storage', definition: 'Largest warehouses with workspace + capacity attribution.' },
          { term: 'Unmeasured warehouses', definition: 'Warehouses whose size could not be measured, with the recorded reason.' },
          { term: 'Row counts & refresh activity', definition: 'Rows from the latest backup; refresh is a snapshot-change proxy (not a true refresh log).' },
        ],
      },
    ],
  },
  'insights.storage': {
    pageTitle: 'Storage Insights',
    purpose:
      'Azure storage footprint, durability, security and cost across the estate — split between fleet (working) and backup storage, with data-grounded cost-optimization findings.',
    sections: [
      {
        title: 'Headline KPIs',
        concepts: [
          { term: 'Managed (GB)', definition: 'All storage under management (fleet + backup), vault-linked accounts excluded so nothing is double-counted.' },
          { term: 'Fleet (GB) vs Backup (GB)', definition: 'Working storage vs storage classified as backup-bound by BINDING evidence (a run/map/plan/copy references it), not the unreliable storage_purpose field.' },
          { term: 'Security Risks', definition: 'Accounts with a genuine misconfiguration — public blob access or a TLS floor below 1.2.' },
          { term: 'Geo-Redundant', definition: 'Accounts on a geo-redundant SKU (GRS/RA-GRS/GZRS) that survive a regional outage, and their share of the fleet.' },
          { term: 'Est. Monthly Cost', definition: 'used GB × per-tier rate from collected rates (dbo.storage_cost_rates, fail-soft) — an estimate, not a billing feed. Shows “—” when no rate is collected.' },
        ],
      },
      {
        title: 'Replication types (durability vs price)',
        paragraphs: [
          'The Replication & durability chart groups accounts by SKU. Multipliers are the data-at-rest price vs LRS (LRS = 1.0×); geo options also add inter-region egress.',
        ],
        concepts: [
          { term: 'LRS (1.0×)', definition: '3 copies in one datacenter — disk/rack failure only.' },
          { term: 'ZRS (~1.25×)', definition: '3 copies across zones in one region — survives a zone outage.' },
          { term: 'GRS (~2.0×)', definition: 'LRS + async copy to a paired region — survives a regional outage (secondary not readable).' },
          { term: 'RA-GRS (~2.5×)', definition: 'GRS with read access to the secondary.' },
          { term: 'GZRS (~2.5×) / RA-GZRS (~3.1×)', definition: 'Zone + geo redundancy; RA variant adds read access to the secondary.' },
        ],
      },
      {
        title: 'Access tiers',
        concepts: [
          { term: 'Hot', definition: 'Highest storage price, lowest read cost — frequently-accessed data.' },
          { term: 'Cool', definition: '~½ the Hot storage price, higher read/retrieval cost + 30-day minimum — short-term backup / infrequently-read data.' },
          { term: 'Archive', definition: 'Cheapest storage but offline: hours-long rehydration + 180-day minimum — cold data you rarely need back.' },
        ],
      },
      {
        title: 'Cost optimization (heuristics)',
        paragraphs: [
          'There is NO per-blob last-access telemetry, so these are honest heuristics, not guarantees — the $ figures come from the collected rates, never fabricated.',
        ],
        concepts: [
          { term: 'Hot → Cool candidates', definition: 'Hot-tier accounts with backup-binding evidence (write-once / read-rarely). Saving = used GB × (Hot − Cool rate). Mind Cool/Archive retrieval + minimum-retention costs before moving.' },
          { term: 'Over-provisioned replication', definition: 'Fleet (non-backup) accounts on GRS/RA-GRS — dropping to LRS saves ~50–60% but loses geo-DR (non-critical data only).' },
          { term: 'Under-protected backups', definition: 'Backup-holding accounts on LRS with no geo-DR — moving to GRS adds a paired-region copy at roughly double the rate.' },
          { term: '“est.” marker', definition: 'A row whose exact SKU × tier rate is not collected, so the estimate fell back to a default rate.' },
        ],
      },
      {
        title: 'Trend & attention lists',
        concepts: [
          { term: 'Fleet growth — 60 days', definition: 'Total measured storage over time from snapshots (days with no reading omitted, never a fake 0).' },
          { term: 'Largest / security-risk / backup-holding accounts', definition: 'Where the footprint is concentrated, which accounts are misconfigured, and which hold backups.' },
        ],
      },
    ],
  },
  'insights.dependencies': {
    pageTitle: 'Dependencies Topology',
    purpose:
      'How capacities, workspaces and shared resources depend on each other — a collapsible tree plus a shared-resource lane.',
    sections: [
      {
        title: 'What this shows',
        concepts: [
          { term: 'Capacity → Workspace → Item', definition: 'A collapsible tree of the estate: each capacity, the workspaces on it, and the items inside — expand to drill down.' },
          { term: 'Capacity Dependencies', definition: 'Declared dependencies between capacities (Required/Optional, with severity) that drive SLA blast radius.' },
          { term: 'Shared-resource lane', definition: 'Resources (storage, capacities) referenced by more than one workspace — where a single failure fans out.' },
        ],
      },
      {
        title: 'How to read it',
        paragraphs: ['Follow the edges down from a node to see its blast radius — what breaks if that node fails. Built entirely from collected relationships (capacity_dependencies, storage maps, per-run bindings); no live graph query.'],
      },
    ],
  },
  'insights.azure-infra': {
    pageTitle: 'Azure Infrastructure Insights',
    purpose:
      'The collected Azure estate behind the platform — resources by type, region and subscription, plus recent drift and orphans.',
    sections: [
      {
        title: 'Headline KPIs',
        concepts: [
          { term: 'Resources', definition: 'Total Azure resources collected into dbo.azure_resources.' },
          { term: 'Subscriptions / Resource Groups / Regions', definition: 'How the estate is spread across subscriptions, resource groups and regions.' },
          { term: 'Added (7d) / Removed (7d)', definition: 'Resources that appeared or disappeared in the last 7 days.' },
        ],
      },
      {
        title: 'Breakdowns & lists',
        concepts: [
          { term: 'Resources by type', definition: 'Grouped by Azure resource type.' },
          { term: 'Region distribution', definition: 'Resource counts per Azure region.' },
          { term: 'Drift feed — last 7 days', definition: 'Recent add/remove changes across the estate.' },
          { term: 'Orphan candidates', definition: 'Resources that look unused/unattached — cleanup candidates.' },
        ],
      },
      {
        title: 'Note',
        paragraphs: ['Detail an Azure API does not expose is shown as "not collected", never faked. Deeper change/drift history is planned as a dedicated feature (Spec 220).'],
      },
    ],
  },
  'insights.cost': {
    pageTitle: 'Cost Analytics',
    purpose:
      'Estimated monthly spend across the estate from collected SKU/retail rates — an estimate, not a billing feed.',
    sections: [
      {
        title: 'Headline spend',
        concepts: [
          { term: 'Capacity Spend / mo', definition: 'Collected capacity SKU rates × the fleet, per month.' },
          { term: 'Storage Spend / mo', definition: 'Per-tier storage rates × measured working footprint.' },
          { term: 'Backup Storage Spend / mo', definition: 'Estimated monthly cost of backup-bound storage.' },
        ],
      },
      {
        title: 'Breakdowns',
        concepts: [
          { term: 'Spend by capacity', definition: 'Estimated monthly cost per capacity — where the money goes.' },
          { term: 'Per-client OneLake attribution', definition: 'Storage/cost attributed to each client via their workspaces.' },
        ],
      },
      {
        title: 'Note',
        paragraphs: ['Costs are estimates from collected list/retail prices (missing rate → shown as not collected), not an Azure billing feed.'],
      },
    ],
  },
  'insights.growth-forecast': {
    pageTitle: 'Growth & Forecast Analytics',
    purpose:
      'Where the estate is trending — projected growth of key metrics from historical daily rollups.',
    sections: [
      {
        title: 'What the numbers mean',
        concepts: [
          { term: 'Forecast', definition: 'A linear-regression projection over dbo.insights_daily_metrics for each metric — a trend estimate, not a guarantee.' },
          { term: 'Weekday-seasonal adjustment', definition: 'A weekday pattern applied on top of the trend line so weekend dips/weekday peaks are reflected.' },
          { term: 'Metrics', definition: 'Item counts, fleet storage, and other daily rollups tracked over time.' },
        ],
      },
      {
        title: '"Not enough history yet"',
        paragraphs: ['A forecast needs enough daily-rollup history to fit a trend. Newly-tracked metrics show this until the self-scheduled daily rollup job has accumulated enough points.'],
      },
    ],
  },
  'governance.data-lineage': {
    pageTitle: 'Data Lineage',
    purpose: 'Collected upstream/downstream lineage between Fabric items and datasets, from the Scanner API.',
    sections: [{ title: 'What this shows', paragraphs: ['Item-to-item and dataset-to-source relationships as last collected. Absent edges mean the source did not expose them, not that none exist.'] }],
  },
  'governance.gateways': {
    pageTitle: 'Gateways',
    purpose: 'On-premises data gateways discovered for the tenant and the datasources bound to them.',
    sections: [{ title: 'What this shows', paragraphs: ['Gateway clusters and their datasource bindings as collected via infra sync. Dev tenants often have none.'] }],
  },
  'governance.shared-artifacts': {
    pageTitle: 'Shared Artifacts',
    purpose: 'Artifacts (datasets, dataflows, models) shared across workspaces, from collected metadata.',
    sections: [{ title: 'What this shows', paragraphs: ['Which artifacts are referenced by more than one workspace — a signal of shared dependencies.'] }],
  },
  'governance.tenant-settings': {
    pageTitle: 'Tenant Settings',
    purpose: 'Collected Fabric/Power BI tenant settings and their enablement state.',
    sections: [{ title: 'What this shows', paragraphs: ['Tenant-level feature switches as last collected; read-only — this page does not change tenant settings.'] }],
  },
  'governance.workspace-access': {
    pageTitle: 'Workspace Access',
    purpose: 'Collected role assignments per workspace — who can access what.',
    sections: [{ title: 'What this shows', paragraphs: ['Workspace role memberships (Admin/Member/Contributor/Viewer) as last collected.'] }],
  },
  'auto.storage-account-detail': {
    pageTitle: 'Storage Account',
    purpose:
      'One Azure storage account in detail: configuration, containers and backup binding.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Configuration', definition: 'Tier, replication and network settings.' },
          { term: 'Containers', definition: 'Blob containers and their usage.' },
          { term: 'Backup binding', definition: 'Whether this account holds backups.' },
        ],
      },
    ],
  },
  'auto.lakehouse-detail': {
    pageTitle: 'Lakehouse',
    purpose:
      'One lakehouse in detail: backup history, recovery points and configuration.',
    sections: [
      {
        title: 'Tabs',
        concepts: [
          { term: 'Overview', definition: 'Workspace, capacity and backup status.' },
          { term: 'Backup history', definition: 'Past runs and their outcomes.' },
          { term: 'Configuration', definition: 'Backup frequency, schedule and exclusions.' },
        ],
      },
    ],
  },
  'auto.warehouse-detail': {
    pageTitle: 'Warehouse',
    purpose:
      'One warehouse in detail: schema, storage, backup history and recovery points.',
    sections: [
      {
        title: 'Tabs',
        concepts: [
          { term: 'Overview', definition: 'Workspace, capacity and status.' },
          { term: 'Schema', definition: 'Tables and structure.' },
          { term: 'Backups', definition: 'Recovery points and history.' },
        ],
      },
    ],
  },
  'auto.client-detail': {
    pageTitle: 'Client',
    purpose:
      'One client in detail: SLA assignment, capacity and workspace links, and blast radius.',
    sections: [
      {
        title: 'Tabs',
        concepts: [
          { term: 'SLA', definition: 'The client tier and any override.' },
          { term: 'Capacities and Workspaces', definition: 'Resources linked to this client.' },
          { term: 'Blast radius', definition: 'What is impacted if this client shared resources fail.' },
        ],
      },
    ],
  },
  'auto.sla-configuration': {
    pageTitle: 'SLA Configuration',
    purpose:
      'SLA tiers and their targets, client assignments, per-client overrides, and maintenance windows.',
    sections: [
      {
        title: 'Tabs',
        concepts: [
          { term: 'SLA Tiers', definition: 'Define tiers and their availability targets.' },
          { term: 'Client Assignments', definition: 'Which tier each client is on.' },
          { term: 'Overrides', definition: 'Per-client target overrides.' },
          { term: 'Maintenance Windows', definition: 'Planned windows excluded from SLA calculations.' },
        ],
      },
    ],
  },
  'auto.backup-section': {
    pageTitle: 'Backup',
    purpose:
      'The command center for backup and restore across lakehouses and warehouses - coverage, plans, schedules, restore, health, recoverability and reports.',
    sections: [
      {
        title: 'The tabs',
        concepts: [
          { term: 'Dashboard', definition: 'Coverage, RPO-derived at-risk assets, and restore-readiness at a glance.' },
          { term: 'Plans', definition: 'First-class backup plans: what is protected, on what cadence, with what retention. Plan resolution is explicit-over-most-specific-over-priority.' },
          { term: 'Management', definition: 'Assign entities to plans, in bulk, and grant the storage access a plan needs.' },
          { term: 'Schedules', definition: 'A graphical schedule builder that compiles to per-backup-type cadences.' },
          { term: 'Restore', definition: 'Browse recovery points and launch a restore into a NEW target, with live progress.' },
          { term: 'Backups', definition: 'Per-entity backup history with per-row tier and retention setters, and a test-restore action.' },
          { term: 'Health', definition: 'Every unprotected entity with a derived reason (never-run, in-progress, capture-failed, unsupported, no-recovery-point).' },
          { term: 'Recoverability', definition: 'Automated recoverability verification and score, secondary copy (3-2-1-1-0), and the isolated vault.' },
          { term: 'Reports', definition: 'Compliance and completion reports across both entity types.' },
          { term: 'Settings', definition: 'Backup engine settings (concurrency caps, flags).' },
        ],
      },
      {
        title: 'Key definitions',
        concepts: [
          { term: 'Protectable population', definition: 'Active, not-restored entities minus exclusions - the ONE denominator coverage is measured against.' },
          { term: 'Protected', definition: 'Entities that are the target of an active plan.' },
          { term: 'Success rate', definition: 'Completed / (Completed + Partial + Failed + Cancelled) - Partial is a first-class outcome, not a success.' },
          { term: 'Recovery point', definition: 'A completed backup you can restore from; each carries its own storage binding, retention and hash.' },
          { term: 'RPO at risk', definition: 'Entities whose newest recovery point is older than their target RPO.' },
        ],
      },
      {
        title: 'Protection features',
        concepts: [
          { term: 'Immutability (WORM)', definition: 'Storage-enforced version-level Locked WORM, opt-in per plan, with the lock derived from retention.' },
          { term: 'Legal hold', definition: 'A hold that prevents pruning past the retention grace period, tracked per run.' },
          { term: 'GFS retention', definition: 'Grandfather-father-son retention with soft-delete, a 7-day grace, and chain-safe pruning.' },
          { term: 'Server-side copy', definition: 'Backups move Azure-to-Azure server-side (no egress through the container) where supported.' },
        ],
      },
    ],
  },
  'auto.data-pipeline-monitoring': {
    pageTitle: 'Data Pipeline Monitoring',
    purpose:
      'Data-movement processes across the estate, driven dynamically by the Universal Logging Engine.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Job activities', definition: 'Per-app, per-job run history from pipelinelogs.' },
          { term: 'Predictability', definition: 'Run-time-growth, missed and hanging-run detection.' },
        ],
      },
    ],
  },
  'auto.lakehouses': {
    pageTitle: 'Lakehouses',
    purpose:
      'All Fabric lakehouses with their backup status and deployment state.',
    sections: [
      {
        title: 'Headline KPIs',
        concepts: [
          { term: 'Total Lakehouses', definition: 'All collected lakehouses.' },
          { term: 'Backup Enabled / Disabled', definition: 'Lakehouses with a backup plan vs without.' },
          { term: 'Deployed / Pending Deploy', definition: 'Deployment state.' },
        ],
      },
      {
        title: 'Note',
        concepts: [
          { term: 'Backups', definition: 'Manage backup configuration in the Backup section.' },
        ],
      },
    ],
  },
  'auto.network-overview': {
    pageTitle: 'Network Topology',
    purpose:
      'Cross-subscription view of Azure networking resources (VNets, subnets, peerings) with CIDR-overlap detection.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'VNets & subnets', definition: 'Address spaces per VNet and subnet, filterable by subscription/resource group.' },
          { term: 'Peerings', definition: 'VNet peerings and their state.' },
          { term: 'CIDR overlap', definition: 'Flags overlapping address spaces that would block peering.' },
        ],
      },
    ],
  },
  'auto.nsg-summary': {
    pageTitle: 'NSG Summary',
    purpose:
      'Network security groups and their rules across the estate.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'NSGs', definition: 'Security groups and the subnets they protect.' },
          { term: 'Rules', definition: 'Inbound and outbound rules per NSG.' },
        ],
      },
    ],
  },
  'auto.pipeline-monitoring': {
    pageTitle: 'Pipeline Monitoring',
    purpose:
      'Curated, ranked health and activity for every pipeline reporting to the Universal Logging Engine — with an admin surface to decide which pipelines matter.',
    sections: [
      {
        title: 'Dashboard',
        concepts: [
          { term: 'Needs attention', definition: 'The lead: failing, dormant, at-risk, and newly-arrived pipelines, ranked by importance so the most consequential problem is first.' },
          { term: 'New arrivals', definition: 'Pipelines reporting for the first time (or a retired one reporting again), awaiting an admin to adopt or retire them.' },
          { term: 'Fleet health', definition: 'A tier-weighted score — a problem on a Critical pipeline moves it far more than one on a Low pipeline.' },
        ],
      },
      {
        title: 'Job Activities',
        concepts: [
          { term: 'Filters', definition: 'Narrow by pipeline, status (with counts), free text, and a time window; sort across the whole matching set. The view is shareable via its link.' },
          { term: 'Export', definition: 'Download the current filtered runs as CSV (Manager/Operator/Admin).' },
        ],
      },
      {
        title: 'Management (admins)',
        concepts: [
          { term: 'Importance tier', definition: 'Critical / Standard / Low — drives ranking, the health score, and alert severity.' },
          { term: 'Retire / reactivate', definition: 'Retiring stops a pipeline counting and alerting (history is preserved); reactivating restores its prior tier.' },
        ],
      },
    ],
  },
  'auto.platform-health': {
    pageTitle: 'Platform Health',
    purpose:
      'FabricControl runtime health, read entirely from collected DB signals (no live probing).',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Components', definition: 'Per-service signal, criterion, and last-collected time.' },
          { term: 'No-probe', definition: 'The webapp never probes Azure; gaps show as not collected.' },
        ],
      },
    ],
  },
  'auto.private-endpoints': {
    pageTitle: 'Private Endpoints',
    purpose:
      'Collected Azure private endpoints and their connection state.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Endpoints', definition: 'Private endpoints per resource and their status.' },
        ],
      },
    ],
  },
  'auto.retention': {
    pageTitle: 'Backup Retention',
    purpose:
      'GFS retention policies and how long each backup is kept before it is pruned.',
    sections: [
      {
        title: 'How retention works',
        concepts: [
          { term: 'GFS policy', definition: 'Grandfather-father-son retention configured per plan (daily/weekly/monthly/yearly keeps).' },
          { term: 'Resolved retention', definition: 'The effective retention after resolving source, then plan, then default.' },
          { term: 'Grace period', definition: 'A 7-day soft-delete grace after expiry before a backup is actually removed.' },
          { term: 'Legal hold', definition: 'A hold that keeps a run past its retention, tracked per run (plan or run sourced).' },
          { term: 'Chain-safe pruning', definition: 'Pruning respects dependency chains and pointer-based recovery points so a restore never loses a needed link.' },
        ],
      },
    ],
  },
  'auto.s-l-a-blast-radius': {
    pageTitle: 'SLA Blast Radius',
    purpose:
      'Simulate the impact of a capacity failure across dependent capacities, workspaces and clients.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Impacted', definition: 'Capacities, workspaces and affected clients downstream of a failure.' },
          { term: 'Dependencies', definition: 'Required or Optional links with severity that drive the simulation.' },
        ],
      },
    ],
  },
  'auto.telemetry': {
    pageTitle: 'Capacity Telemetry',
    purpose:
      'The Eventstream capacity-metrics pipeline and its live CU telemetry feed.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Pipeline health', definition: 'Whether the telemetry Eventstream is flowing per capacity.' },
          { term: 'Live CU', definition: 'Streaming utilization readings that feed Capacity Insights.' },
        ],
      },
    ],
  },
  'auto.warehouses': {
    pageTitle: 'Warehouses',
    purpose:
      'All Fabric SQL warehouses under management, with type and active/inactive status.',
    sections: [
      {
        title: 'Headline KPIs',
        concepts: [
          { term: 'Total Warehouses', definition: 'All collected warehouses.' },
          { term: 'Active / Inactive', definition: 'Warehouses currently active vs not.' },
        ],
      },
      {
        title: 'Note',
        concepts: [
          { term: 'Detail', definition: 'Click a warehouse for its schema, storage and backup detail.' },
        ],
      },
    ],
  },
  'auto.workspaces': {
    pageTitle: 'Workspaces',
    purpose:
      'Every Fabric workspace under management, with its capacity, subscription, storage and semantic-model counts.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Columns', definition: 'Capacity, Subscription, Storage, Semantic Models and Status for each workspace.' },
          { term: 'With Semantic Models', definition: 'How many workspaces contain at least one semantic model.' },
          { term: 'Capacity Spread', definition: 'How workspaces are distributed across capacities.' },
        ],
      },
      {
        title: 'Actions',
        concepts: [
          { term: 'Sync', definition: 'Synchronize workspace data from Microsoft Fabric.' },
          { term: 'Filter/Search', definition: 'All filters are server-side — search plus capacity/subscription/has-semantic-models.' },
        ],
      },
    ],
  },
  'auto.alert-rules': {
    pageTitle: 'Alert Rules',
    purpose:
      'Alert rules and their targeting across capacities, backups, storage and pipelines.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Rule config', definition: 'Condition and severity per rule.' },
          { term: 'Targeting', definition: 'Global, per-capacity or per-entity scope via the rule junction.' },
        ],
      },
    ],
  },
  'auto.alerts': {
    pageTitle: 'Alerts',
    purpose:
      'All fired alerts across the platform - capacities, backups, and infrastructure. Review, acknowledge, and resolve.',
    sections: [
      {
        title: 'Columns',
        concepts: [
          { term: 'Severity', definition: 'Critical/Warning/etc.' },
          { term: 'Type', definition: 'What the alert is about (capacity, backup, infra, pipeline).' },
          { term: 'Status', definition: 'Active vs acknowledged/resolved.' },
          { term: 'Created', definition: 'When it fired.' },
        ],
      },
      {
        title: 'Actions',
        concepts: [
          { term: 'Acknowledge', definition: 'Select alerts and mark them acknowledged.' },
        ],
      },
    ],
  },
  'auto.auth-audit-log': {
    pageTitle: 'Authentication Audit Log',
    purpose:
      'Sign-in and authentication events across the platform.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Events', definition: 'Who signed in, when, and from where.' },
          { term: 'Filters', definition: 'Filter by user, outcome and time.' },
        ],
      },
    ],
  },
  'auto.capacity-detail': {
    pageTitle: 'Capacity Detail',
    purpose:
      'One Fabric capacity in detail: utilization, attribution, and the workspaces and items it carries.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Utilization', definition: 'Recent CU% for this capacity.' },
          { term: 'Attribution', definition: 'Storage and item counts derived live from dbo, not the never-written attribution table.' },
          { term: 'Workspaces and items', definition: 'What runs on this capacity.' },
        ],
      },
    ],
  },
  'auto.capacity-metrics': {
    pageTitle: 'Capacity Metrics',
    purpose:
      'Fabric capacity utilization, trends and alerts from collected CU telemetry.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Utilization', definition: 'CU% per capacity over time.' },
          { term: 'Trends', definition: 'How utilization is moving.' },
          { term: 'Alerts', definition: 'Capacity threshold alerts.' },
        ],
      },
    ],
  },
  'auto.clients': {
    pageTitle: 'Clients',
    purpose:
      'Every client synced from the OneWare organization directory, with SLA tier and linked workspaces.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'SLA tier', definition: 'The service-level tier assigned to each client (default Standard).' },
          { term: 'Linked workspaces', definition: 'Workspaces bound to the client, feeding blast-radius.' },
          { term: 'Identity', definition: 'Client records are sync-only from the source directory; they are not manually created here.' },
        ],
      },
    ],
  },
  'auto.delta-compare': {
    pageTitle: 'Delta Compare',
    purpose:
      'Compare a backup against current lakehouse data, table by table, to see what changed.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Table results', definition: 'Row-count and presence differences per table.' },
          { term: 'Past comparisons', definition: 'Previously run comparisons you can rehydrate.' },
        ],
      },
    ],
  },
  'auto.health-dashboard': {
    pageTitle: 'Infrastructure Health',
    purpose:
      'Azure-estate health from collected heartbeat and platform-health snapshots.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Signals', definition: 'Reachability and status per component, DB-collected.' },
          { term: 'No-probe', definition: 'Gaps show as not collected rather than a live check.' },
        ],
      },
    ],
  },
  'auto.item-schedules': {
    pageTitle: 'Item Schedules',
    purpose:
      'Collected Fabric pipeline and notebook schedules for an item.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Schedules', definition: 'Configured run schedules and next run time.' },
        ],
      },
    ],
  },
  'auto.lineage-graph': {
    pageTitle: 'Lineage Graph',
    purpose:
      'A visual graph of item upstream and downstream dependencies.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Nodes and edges', definition: 'Each node is an item or dataset; edges are collected lineage relationships.' },
        ],
      },
    ],
  },
  'auto.lineage-list': {
    pageTitle: 'Data Lineage',
    purpose:
      'Collected upstream and downstream lineage between items and datasets.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Lineage', definition: 'Item-to-source relationships from the Scanner API; missing edges mean the source did not expose them.' },
        ],
      },
    ],
  },
  'auto.metadata-explorer': {
    pageTitle: 'Metadata Explorer',
    purpose:
      'Semantic-model definitions, lineage, refresh history and item schedules collected via the Scanner API.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Semantic models', definition: 'Definitions, measures and refresh state.' },
          { term: 'Lineage and schedules', definition: 'Cross-links and configured schedules.' },
        ],
      },
    ],
  },
  'auto.refresh-history': {
    pageTitle: 'Refresh History',
    purpose:
      'Dataset refresh runs and schedules for a semantic model.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Runs', definition: 'Recent refresh outcomes and durations.' },
          { term: 'Schedule', definition: 'The configured refresh cadence.' },
        ],
      },
    ],
  },
  'auto.refresh-history-list': {
    pageTitle: 'Refresh History',
    purpose:
      'Datasets and their latest refresh state across the estate.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Latest refresh', definition: 'Per-dataset last outcome; drill in for the full run history.' },
        ],
      },
    ],
  },
  'auto.schedules-list': {
    pageTitle: 'Schedules',
    purpose:
      'Collected pipeline and item schedules across the estate.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Schedules', definition: 'Configured schedules with cadence and next run.' },
        ],
      },
    ],
  },
  'auto.semantic-model-definition': {
    pageTitle: 'Semantic Model',
    purpose:
      'One semantic model in detail: measures, tables, relationships and refresh.',
    sections: [
      {
        title: 'Tabs',
        concepts: [
          { term: 'Definition', definition: 'Measures, tables and relationships from the Scanner API.' },
          { term: 'Refresh', definition: 'Recent refresh history.' },
        ],
      },
    ],
  },
  'auto.semantic-models-list': {
    pageTitle: 'Semantic Models',
    purpose:
      'Collected semantic models with definition, refresh and row-count coverage.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Coverage', definition: 'Which models have a captured definition, refresh history, and measured row count.' },
        ],
      },
    ],
  },
  'auto.system-audit-log': {
    pageTitle: 'System Audit Log',
    purpose:
      'Actions taken across the platform, captured by the central audit middleware.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Actions', definition: 'Humanized action labels with actor and target.' },
          { term: 'Filters', definition: 'Filter by action type and time.' },
        ],
      },
    ],
  },
  'auto.users': {
    pageTitle: 'User Management',
    purpose:
      'Platform users and their role assignments (Admin/Manager/Operator/Viewer).',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Role', definition: 'The role assigned to each user, which gates route and menu access.' },
          { term: 'Last login', definition: 'When each user last signed in.' },
        ],
      },
    ],
  },
  'auto.workflow': {
    pageTitle: 'Workflows',
    purpose:
      'Long-running platform workflows and their progress.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Workflows', definition: 'In-flight and recent workflows; completed hidden by default with a show-completed toggle.' },
        ],
      },
    ],
  },
  'auto.workspace-form': {
    pageTitle: 'Workspace',
    purpose:
      'Add or edit a Fabric workspace and its backup configuration.',
    sections: [
      {
        title: 'What you set',
        concepts: [
          { term: 'Identity', definition: 'The workspace name and identifiers.' },
          { term: 'Backup configuration', definition: 'Whether definitions are captured and how the workspace participates in backup plans.' },
        ],
      },
    ],
  },
  'auto.infrastructure-trends': {
    pageTitle: 'Infrastructure Trends',
    purpose:
      'Trends and anomaly detection over collected Azure infrastructure metrics.',
    sections: [
      {
        title: 'What you see',
        concepts: [
          { term: 'Trends', definition: 'Metric trends over time.' },
          { term: 'Anomalies', definition: 'Detected outliers, with export.' },
        ],
      },
    ],
  },
  'auto.storage-accounts': {
    pageTitle: 'Storage Accounts',
    purpose:
      'ARM-discovered Azure storage accounts across all subscriptions, with tier, replication and container counts.',
    sections: [
      {
        title: 'Headline KPIs',
        concepts: [
          { term: 'Total Accounts', definition: 'All collected storage accounts.' },
          { term: 'Total Storage', definition: 'Sum of measured account sizes.' },
          { term: 'With Containers', definition: 'Accounts that have at least one blob container.' },
        ],
      },
      {
        title: 'Columns',
        concepts: [
          { term: 'Access Tier', definition: 'Hot/Cool/Archive tier.' },
          { term: 'Replication / Geo-Redundant', definition: 'LRS/GRS/etc. and whether the account is geo-redundant.' },
          { term: 'Resource Group / Subscription', definition: 'Where the account lives in Azure.' },
        ],
      },
    ],
  },
  // Spec 285 — SuperAdmin-only license administration.
  'admin.tenant-licenses': {
    pageTitle: 'Tenant Licenses',
    purpose:
      'See one tenant\'s licence as Fabric Control Management (FCM) issued it. The licence decides whether that tenant\'s users see, and can reach, the licensed features at all. This page is read-only: licences are changed in FCM.',
    sections: [
      {
        title: 'What the licence holds',
        concepts: [
          { term: 'Status', definition: 'Active, Suspended or Revoked. Only an active licence turns anything on; a suspended or revoked tenant sees a notice page instead of the app.' },
          { term: 'Tier and limits', definition: 'The tier FCM issued, and the most workspaces, items and capacities the tenant may have. "No limit" means FCM set none. When a limit is reached, new items stop being added from that date (the growth freeze); existing ones keep updating.' },
          { term: 'Backup add-on', definition: 'Everything backup: Backup Management (every tab), Workflows (backup onboarding), the backup tiles on the dashboards, and the backup views inside Resources — the Protection tabs and panels, a lakehouse\'s backup history, storage mapping and backup configuration. Without it those are hidden and their API endpoints refuse access.' },
          { term: 'Ops add-on', definition: 'Pipeline Monitoring. Without it the page is hidden and its API endpoints refuse access.' },
          { term: 'In force', definition: 'An add-on is in force when it is included, its term has not ended, and the licence is active.' },
          { term: 'No licence', definition: 'The tenant has no licence row, so nothing paid is visible.' },
        ],
      },
      {
        title: 'How changes take effect',
        paragraphs: [
          'You view one tenant at a time. The page opens on your current tenant; enter another tenant id to view that one instead.',
          'A licence is changed in FCM, which writes it to this platform. The change applies to the tenant\'s users on their next request, with no redeploy. As a SuperAdmin you always see every feature, whatever the tenant\'s licence.',
        ],
      },
    ],
  },

  // ── Spec 285 — Internal pages (affordance gaps closed) ──────────────────────
  'internal.infrastructure-sync': {
    pageTitle: 'Infrastructure Sync',
    purpose:
      'The history of infrastructure sync runs — the discovery pass that inventories your Azure and Fabric estate — and how complete each run was. Runs come from the worker/brain collection schedule; there is no on-demand sync.',
    sections: [
      {
        title: 'Reading the run history',
        concepts: [
          { term: 'Status', definition: 'The outcome of the sync job only: Completed, Running, Failed or Partial. A failed run shows the start of its error; hover for the full text.' },
          { term: 'Merge badge', definition: 'The merge is a separate job that moves collected rows into the platform tables. "merge: N tables" means the data landed; "failed" or "held by guard" means it did not; "none found" means no merge was recorded in the 30 minutes after the run — that is not a clean merge, it is an unconfirmed one.' },
          { term: 'Trigger', definition: '"Scheduled". Older runs may read "Manual (who)" from the retired Scan Now button. "—" means the run predates trigger tracking.' },
          { term: 'Entities', definition: 'The inventory recorded by that run. "—" means the run did not record it.' },
          { term: 'Changes', definition: 'How many items were added, updated or removed. Click a run to see them. "not derived" means the inventory moved but no changes were computed — the change pass runs after the merge, so check the merge for that run.' },
        ],
      },
      {
        title: 'Completeness and coverage',
        paragraphs: [
          'The integrity panel shows, per stage, how much of the attempted work succeeded and which problems are open; click a stage or a problem for the breakdown and the fix. Percentages measure the named unit only — a stage can be at 100% on its main unit and still be degraded.',
          'Access-gap coverage compares what the sync can see with what Azure bills. "Never assessed" and "Cannot assess" mean no comparison was made — not that no gaps exist.',
        ],
      },
      {
        title: 'Refresh',
        paragraphs: [
          'Refresh reloads the run history, the sync status and the integrity panel. They also refresh on their own every 30–60 seconds.',
        ],
      },
    ],
  },

  'internal.collection-jobs': {
    pageTitle: 'Collection Jobs',
    purpose:
      'What the data collection is doing now, and the history of its runs — when each ran, which tier it was, how long it took, how it ended and what it collected.',
    sections: [
      {
        title: 'Current status',
        paragraphs: [
          'While a run is in progress you see its current step, a progress bar and the entities and errors so far. A run still going after two hours is flagged as stale. When nothing is running you see the last run\'s end time, outcome, entities and duration.',
        ],
      },
      {
        title: 'Run history',
        concepts: [
          { term: 'Tier', definition: 'The schedule the run belongs to — daily, 2x-daily, 4x-daily, hourly, 30min, 15min or 5min. Use the tier buttons to show one tier only.' },
          { term: 'Status', definition: 'Completed, Partial, Failed, or Running.' },
          { term: 'Run detail', definition: 'Click a run to see the entities it collected by type and its steps. A step marked red had errors, amber had warnings; click it for the first errors.' },
        ],
      },
      {
        title: 'Starting a run and Refresh',
        paragraphs: [
          'Operators and above can start a full collection with Start Collection; it is unavailable while a run is in progress, and typically takes around 45 minutes.',
          'Refresh reloads the status, the run history and the details of any run you have opened. The status also refreshes on its own — every 15 seconds while a run is in progress, every minute otherwise.',
        ],
      },
    ],
  },

  'internal.collection-job-logs': {
    pageTitle: 'Collection Job Logs',
    purpose:
      'The step-by-step record of every collection run: which steps ran, how long each took, and the structured log entries each step wrote.',
    sections: [
      {
        title: 'Reading a run',
        concepts: [
          { term: 'Steps', definition: 'Completed of total steps; a red count in brackets is how many steps failed.' },
          { term: 'Entities', definition: 'Entities discovered, then the error (E) and warning (W) counts. Hover for updated and snapshot counts.' },
          { term: 'Status', definition: 'Running, Completed, Partial or Failed.' },
        ],
      },
      {
        title: 'Drilling in',
        paragraphs: [
          'Click a run to list its steps; click a step to read its log entries. Filter the entries by severity — DEBUG, INFO, WARNING, ERROR or CRITICAL. A step shows up to 100 entries at a time.',
          'Refresh reloads the run list and the steps and entries of any run you have opened; the run list also refreshes on its own every 30 seconds. Previous and Next page through older runs, 20 at a time.',
        ],
      },
    ],
  },

  'internal.deployment-history': {
    pageTitle: 'Deployment History',
    purpose:
      'A read-only audit trail of every deployment, written automatically by the deployment script — which component went to which environment, with what image and commit, by whom, and whether it succeeded.',
    sections: [
      {
        title: 'Columns',
        concepts: [
          { term: 'Component', definition: 'What was deployed: collection, backup, web, database, logging, brain or collection-worker.' },
          { term: 'Environment', definition: 'dev or prod (prod is highlighted).' },
          { term: 'Image Tag / Git SHA', definition: 'The exact image and the commit it was built from. Hover for the full value; the commit reads "unknown" when it was not recorded.' },
          { term: 'Deployed By', definition: 'The git user who ran the deployment, or "system".' },
          { term: 'Status / Duration', definition: 'Whether the deployment succeeded or failed, and how long it took.' },
        ],
      },
      {
        title: 'Filtering',
        paragraphs: [
          'Filter by environment, component and a date range (both dates inclusive); 50 records per page. Times are shown in Eastern time. Nothing can be created or deleted here.',
          'Refresh reloads the list; it does not refresh on its own.',
        ],
      },
    ],
  },

  'internal.service-principal-detail': {
    pageTitle: 'Service Principal',
    purpose:
      'One registered service principal: its identity, when it was last verified, and the workspace permissions it is expected to hold — with any drift between expected and actual.',
    sections: [
      {
        title: 'Details and status',
        paragraphs: [
          'Details show the application and object ids, purpose, the Key Vault secret it uses and whether drift is fixed automatically. Status shows when it was last verified in Entra ID and in Fabric, and how many verifications in a row have failed.',
        ],
      },
      {
        title: 'Workspace assignments and drift',
        concepts: [
          { term: 'OK', definition: 'The permission matches what is expected.' },
          { term: 'Permission Missing / SP Deleted / Fix Failed', definition: 'Critical: the expected access is not there, the service principal no longer exists in Entra ID, or an automatic fix did not work.' },
          { term: 'Permission Elevated / Untracked', definition: 'Warning: it holds more access than expected, or a permission that is not tracked here.' },
          { term: 'Auto-Fixed / Manually Resolved', definition: 'A past drift that has been closed.' },
        ],
      },
      {
        title: 'Actions',
        paragraphs: [
          'Edit changes its details. Add Assignment records a workspace permission it should hold; Remove deletes one after you confirm. Delete is a soft delete — the record is marked deleted and its assignments are kept for audit. Once it is deleted, the Edit, Delete and Add Assignment buttons are disabled.',
          'Refresh reloads the service principal and its assignments.',
        ],
      },
    ],
  },
};

/** Resolve help content for a pageId. Returns undefined when unregistered. */
export function getPageHelp(pageId: string): PageHelpContent | undefined {
  return pageHelp[pageId];
}
